'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { validateDates } from '@utils/date-helpers';
import { routes } from '@routes';

/**
 * BHT-6311: parses the booking-widget URL state for the
 * `<ListingQuotesSection>` Suspense island and enforces invalid-date
 * parity with the previously server-side `validateDates -> redirect`
 * block in [page.tsx](../../page.tsx).
 *
 * Extracted so the parsing rules + redirect side-effect can be unit
 * tested in isolation; the component itself just consumes the result.
 */
export interface BookingWidgetSearchParams {
  adultCount: number;
  childCount: number;
  infantCount: number;
  petCount: number;
  checkInDate: string | null;
  checkOutDate: string | null;
}

// CodeRabbit on PR #2083: `parseInt` silently truncates partially
// numeric strings (`"2abc" -> 2`, `"1.5" -> 1`, `"1e2" -> 1`), which
// for URL-derived booking state would quietly mutate the guest mix.
// Lock the raw string to a non-empty digit run so any malformed value
// falls back to `defaultValue` instead.
const COUNT_RE = /^\d+$/;
const parseCountParam = (value: string | null | undefined, defaultValue: number): number => {
  if (!value || !COUNT_RE.test(value)) return defaultValue;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : defaultValue;
};
export function useBookingWidgetSearchParams(slug) {
  const $ = _c(25);
  const searchParams = useSearchParams();
  const router = useRouter();
  let t0;
  if ($[0] !== searchParams) {
    t0 = parseCountParam(searchParams?.get("adultCount"), 1);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const adultCount = t0;
  let t1;
  if ($[2] !== searchParams) {
    t1 = parseCountParam(searchParams?.get("childCount"), 0);
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const childCount = t1;
  let t2;
  if ($[4] !== searchParams) {
    t2 = parseCountParam(searchParams?.get("infantCount"), 0);
    $[4] = searchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const infantCount = t2;
  let t3;
  if ($[6] !== searchParams) {
    t3 = parseCountParam(searchParams?.get("petCount"), 0);
    $[6] = searchParams;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const petCount = t3;
  let hasUnparsableDate;
  let rawCheckInDate;
  let rawCheckOutDate;
  let t4;
  if ($[8] !== searchParams) {
    rawCheckInDate = searchParams?.get("checkInDate") || null;
    rawCheckOutDate = searchParams?.get("checkOutDate") || null;
    const parseDate = _temp;
    const parsedCheckIn = parseDate(rawCheckInDate);
    const parsedCheckOut = parseDate(rawCheckOutDate);
    const hasBothDatesPresent = !!rawCheckInDate && !!rawCheckOutDate;
    hasUnparsableDate = hasBothDatesPresent && (parsedCheckIn === null || parsedCheckOut === null);
    t4 = hasBothDatesPresent && parsedCheckIn !== null && parsedCheckOut !== null && !validateDates(parsedCheckIn, parsedCheckOut);
    $[8] = searchParams;
    $[9] = hasUnparsableDate;
    $[10] = rawCheckInDate;
    $[11] = rawCheckOutDate;
    $[12] = t4;
  } else {
    hasUnparsableDate = $[9];
    rawCheckInDate = $[10];
    rawCheckOutDate = $[11];
    t4 = $[12];
  }
  const hasReversedRange = t4;
  const hasInvalidDateRange = hasUnparsableDate || hasReversedRange;
  const checkInDate = hasInvalidDateRange ? null : rawCheckInDate;
  const checkOutDate = hasInvalidDateRange ? null : rawCheckOutDate;
  let t5;
  let t6;
  if ($[13] !== hasInvalidDateRange || $[14] !== router || $[15] !== slug) {
    t5 = () => {
      if (hasInvalidDateRange) {
        router.replace(routes.listings({
          slug
        }));
      }
    };
    t6 = [hasInvalidDateRange, slug, router];
    $[13] = hasInvalidDateRange;
    $[14] = router;
    $[15] = slug;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  React.useEffect(t5, t6);
  let t7;
  if ($[18] !== adultCount || $[19] !== checkInDate || $[20] !== checkOutDate || $[21] !== childCount || $[22] !== infantCount || $[23] !== petCount) {
    t7 = {
      adultCount,
      childCount,
      infantCount,
      petCount,
      checkInDate,
      checkOutDate
    };
    $[18] = adultCount;
    $[19] = checkInDate;
    $[20] = checkOutDate;
    $[21] = childCount;
    $[22] = infantCount;
    $[23] = petCount;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
function _temp(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}